import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, MapPin, ChevronDown, User, Gauge, Wifi } from 'lucide-react';
import { cities, companyInfo } from '../data/mock';

const Header = ({ selectedCity, onCityChange }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMenuOpen(false);
  };

  const navItems = [
    { label: 'Início', id: 'hero' },
    { label: 'Planos', id: 'planos' },
    { label: 'Vantagens', id: 'vantagens' },
    { label: 'FAQ', id: 'faq' },
  ];

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-[#0a1628]/95 backdrop-blur-xl shadow-lg shadow-blue-500/10 py-3'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <motion.div 
            className="flex items-center gap-3"
            whileHover={{ scale: 1.05 }}
          >
            <img
              src={companyInfo.logo}
              alt="GFA Telecom"
              className="h-10 md:h-12 w-auto object-contain brightness-0 invert"
            />
          </motion.div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item, index) => (
              <motion.button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="relative px-4 py-2 text-gray-300 hover:text-white font-medium transition-colors group"
                whileHover={{ scale: 1.05 }}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {item.label}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-gradient-to-r from-blue-500 to-cyan-400 group-hover:w-full transition-all duration-300" />
              </motion.button>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* City Selector */}
            <div className="relative">
              <motion.button
                onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 hover:border-blue-500/50 hover:bg-white/10 transition-all duration-300"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <MapPin className="w-4 h-4 text-cyan-400" />
                <span className="text-gray-300 text-sm">
                  {selectedCity?.name || 'Sua cidade'}
                </span>
                <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isCityDropdownOpen ? 'rotate-180' : ''}`} />
              </motion.button>

              <AnimatePresence>
                {isCityDropdownOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className="absolute top-full right-0 mt-2 w-56 bg-[#0a1628]/95 backdrop-blur-xl rounded-xl border border-white/10 shadow-xl shadow-black/50 overflow-hidden"
                  >
                    {cities.map((city, index) => (
                      <motion.button
                        key={city.id}
                        onClick={() => {
                          onCityChange(city);
                          setIsCityDropdownOpen(false);
                        }}
                        className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors ${
                          selectedCity?.id === city.id ? 'bg-blue-500/10 text-cyan-400' : 'text-gray-300'
                        }`}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <MapPin className="w-4 h-4" />
                        <span className="text-sm">{city.name}</span>
                        {selectedCity?.id === city.id && (
                          <div className="ml-auto w-2 h-2 rounded-full bg-cyan-400" />
                        )}
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Central do Assinante */}
            <motion.button
              onClick={() => window.open('https://gfanet.com.br/cliente', '_blank')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-blue-500/50 text-blue-400 hover:bg-blue-500/10 transition-all duration-300"
              whileHover={{ scale: 1.02, borderColor: 'rgba(59, 130, 246, 0.8)' }}
              whileTap={{ scale: 0.98 }}
            >
              <User className="w-4 h-4" />
              <span className="text-sm font-medium">Central do Assinante</span>
            </motion.button>

            {/* Teste de Velocidade */}
            <motion.button
              onClick={() => scrollToSection('speedtest')}
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-medium shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Gauge className="w-4 h-4" />
              <span className="text-sm">Teste sua Velocidade</span>
            </motion.button>
          </div>

          {/* Mobile Menu Button */}
          <motion.button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 rounded-xl bg-white/5 border border-white/10"
            whileTap={{ scale: 0.9 }}
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-white" />
            ) : (
              <Menu className="w-6 h-6 text-white" />
            )}
          </motion.button>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden mt-4 overflow-hidden"
            >
              <div className="bg-[#0a1628]/95 backdrop-blur-xl rounded-2xl border border-white/10 p-4 space-y-4">
                {/* City Selector Mobile */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wider px-2">Selecione sua cidade</p>
                  <div className="grid grid-cols-2 gap-2">
                    {cities.map((city) => (
                      <motion.button
                        key={city.id}
                        onClick={() => {
                          onCityChange(city);
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedCity?.id === city.id
                            ? 'bg-gradient-to-r from-blue-600 to-cyan-500 text-white'
                            : 'bg-white/5 text-gray-400 hover:bg-white/10'
                        }`}
                        whileTap={{ scale: 0.95 }}
                      >
                        {city.name}
                      </motion.button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-white/10 pt-4 space-y-1">
                  {navItems.map((item) => (
                    <motion.button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="block w-full text-left px-4 py-3 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-colors"
                      whileTap={{ scale: 0.98 }}
                    >
                      {item.label}
                    </motion.button>
                  ))}
                </div>

                <div className="border-t border-white/10 pt-4 space-y-2">
                  <motion.button
                    onClick={() => window.open('https://gfanet.com.br/cliente', '_blank')}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-blue-500/50 text-blue-400"
                    whileTap={{ scale: 0.98 }}
                  >
                    <User className="w-4 h-4" />
                    Central do Assinante
                  </motion.button>
                  <motion.button
                    onClick={() => {
                      scrollToSection('speedtest');
                      setIsMenuOpen(false);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-medium"
                    whileTap={{ scale: 0.98 }}
                  >
                    <Gauge className="w-4 h-4" />
                    Teste de Velocidade
                  </motion.button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};

export default Header;
